import React, { FC, useCallback, useMemo } from 'react';

import { NumberChartResponse, ReportChart } from '@app/interfaces/report.type';

import ReactApexChart from 'react-apexcharts';

import useTranslation from '@app/hooks/use-translation';

import NumberChart from './number-chart';
import { chartBuildData } from './chart-build.data';
import { ApexPointEvent, onChartPointHandleClick } from './utils';

const tooltipOffsetX = {
  8: -5,
  7: -10,
  6: -15,
  5: -20,
  4: -25,
  3: -30,
  2: -35,
};

const ChartMainRenderer: FC<{
  chartHeight: number;
  isNumber: boolean;
  data: ReportChart | NumberChartResponse;
  widgetMode: boolean;
  disableDataPointClick: boolean;
  visibleToolbarState: boolean;
  chartWidth: number;
  currentTemplate: 'chat' | 'chats' | 'voice';
}> = ({
  isNumber,
  data,
  widgetMode,
  disableDataPointClick,
  chartWidth,
  visibleToolbarState,
  currentTemplate,
}) => {
  const { t } = useTranslation('pages.chartPage');
  // constants
  const translationList = useMemo(
    () => ({
      download: t('chart_toolbar.download'),
      selectionZoom: t('chart_toolbar.selectionZoom'),
      zoomIn: t('chart_toolbar.zoomIn'),
      zoomOut: t('chart_toolbar.zoomOut'),
      pan: t('chart_toolbar.pan'),
      reset: t('chart_toolbar.reset'),
    }),
    [t],
  );
  const getNoDataText = useCallback(
    (status?: number) => {
      switch (status) {
        case 500:
          return t('system.no_metric');

        case 403:
          return t('system.no_access');

        default:
          return t('system.no_data');
      }
    },
    [t],
  );

  const handlePointSelect = useCallback(
    (chartPointInfo: ApexPointEvent) => {
      if (
        !chartPointInfo?.w?.config?.chart?.toolbar?.tools?.pan &&
        localStorage.getItem('layoutDrag') !== 'true' &&
        !disableDataPointClick
      ) {
        onChartPointHandleClick(chartPointInfo, {
          data: data as ReportChart,
          currentTemplate,
        });
      }
    },
    [currentTemplate, data, disableDataPointClick],
  );

  const chartEvents = useMemo(
    () => ({
      markerClick: (_: unknown, __: unknown, info: ApexPointEvent) => handlePointSelect(info),

      dataPointSelection: (_: unknown, __: unknown, info: ApexPointEvent) =>
        handlePointSelect(info),
    }),
    [handlePointSelect],
  );
  const tooltipDynamicOffset = useMemo(
    () => tooltipOffsetX[Math.floor(chartWidth / 100)] ?? 0,
    [chartWidth],
  );
  // constants
  const currentChartBuild = useMemo(() => {
    if (isNumber || data.type === 'number') {
      return undefined;
    }

    return chartBuildData({
      chart: data,
      showToolbar: visibleToolbarState,
      tooltipDynamicOffset,
      tooltipCustomOffset: widgetMode ? -24 : undefined,
      noDataText: {
        text: getNoDataText(data.errorStatus),
      },
      chartWidth,
      events: chartEvents,
      translationList,
      widgetMode,
    });
  }, [
    isNumber,
    data,
    visibleToolbarState,
    tooltipDynamicOffset,
    widgetMode,
    chartWidth,
    chartEvents,
    translationList,
    getNoDataText,
  ]);
  const options = useMemo(() => currentChartBuild?.options, [currentChartBuild?.options]);
  const series = useMemo(() => currentChartBuild?.series, [currentChartBuild?.series]);
  const type = useMemo(
    () => currentChartBuild?.options.chart?.type,
    [currentChartBuild?.options.chart?.type],
  );

  React.useEffect(() => {
    if (type !== 'heatmap') return;
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }, [type]);

  if (!isNumber && (!currentChartBuild || !options || !series)) {
    return null;
  }
  return (
    <div className={`${isNumber ? 'w-full h-full' : 'w-[95%] h-[99%]'}`}>
      <div className="h-full w-full">
        {isNumber ? (
          <NumberChart data={data as NumberChartResponse} />
        ) : (
          <ReactApexChart
            key={options?.chart.id}
            options={options ?? {}}
            series={series ?? []}
            width="100%"
            height="100%"
            type={type}
          />
        )}
      </div>
    </div>
  );
};

export default React.memo(ChartMainRenderer);
